import {
    mxCell,
    mxClient,
    mxConstants,
    mxDragSource,
    mxEvent,
    mxEventObject,
    mxGeometry,
    mxPoint,
    mxRectangle,
    mxResources,
    mxStackLayout,
    mxUtils,
} from './mxGraphFactory';
import {Editor} from './Editor';
import {HoverIcons} from './Graph';
import {
    createAIChatTask,
    createJsonDocumentReaderTask,
    createJsonConverterTask,
    createTextDocumentReaderTask,
    createHtmlDocumentReaderTask,
    createMarkdownDocumentReaderTask,
    createYamlDocumentReaderTask,
    createAIVectorStoreTask,
    createAIVectorSearchTask,
    createAIVectorDeleteTask,
    createEmailTask,
    createHttpTask,
    createJQTask,
    createMapTask,
    createSimpleTask,
    createTerminateTask,
    createWaitTask,
} from './helper';

import '@mdi/svg/svg/account-outline.svg';
import '@mdi/svg/svg/cog-transfer-outline.svg';
import '@mdi/svg/svg/robot-outline.svg';
import '@mdi/svg/svg/timer-outline.svg';
import '@mdi/svg/svg/email-outline.svg';
import '@mdi/svg/svg/state-machine.svg';
import '@mdi/svg/svg/database-plus.svg';
import '@mdi/svg/svg/code-json.svg';
import '@mdi/svg/svg/file-document-outline.svg';
import '@mdi/svg/svg/language-html5.svg';
import '@mdi/svg/svg/language-markdown.svg';
import '@mdi/svg/svg/database-search.svg';
import '@mdi/svg/svg/database-minus.svg';
import '../../images/logo-jq.png';

/**
 * Constructs a new palette for the given editor.
 */
export const Palette = function (editorUi, container) {
    this.editorUi = editorUi;
    this.container = container;
    this.palettes = {};
    this.graph = editorUi.createTemporaryGraph(this.editorUi.editor.graph.getStylesheet());
    this.graph.cellRenderer.antiAlias = false;
    this.graph.foldingEnabled = false;
    this.graph.container.style.visibility = 'hidden';

    this.init();
};

/**
 * Adds all palettes to the pallete.
 */
Palette.prototype.init = function () {
    this.addWorkflowPalette();
};

/**
 *
 */
Palette.prototype.dragPreviewBorder = '1px dashed black';

/**
 * Specifies the delay for the drop target icons. Default is 200 ms.
 */
Palette.prototype.dropTargetDelay = 200;

/**
 * Specifies the height of the thumbnails.
 */
Palette.prototype.thumbHeight = 26.5;

const taskWidth = 190;
const suppressedDragElementClassName = 'gePaletteDragGhostSuppressor';

/**
 * Adds the Workflow library.
 */
Palette.prototype.addWorkflowPalette = function () {
    const fns = [
        this.createVertexTemplateEntry('task', taskWidth, 40, createSimpleTask(mxResources.get('task'))),
        this.createVertexTemplateEntry('http', taskWidth, 40, createHttpTask(mxResources.get('http'))),
        this.createVertexTemplateEntry('ai-chat', taskWidth, 40, createAIChatTask(mxResources.get('aiChatTask'))),
        this.createVertexTemplateEntry('json-document-reader', taskWidth, 40, createJsonDocumentReaderTask(mxResources.get('jsonDocumentReaderTask'))),
        this.createVertexTemplateEntry('text-document-reader', taskWidth, 40, createTextDocumentReaderTask(mxResources.get('textDocumentReaderTask'))),
        this.createVertexTemplateEntry('html-document-reader', taskWidth, 40, createHtmlDocumentReaderTask(mxResources.get('htmlDocumentReaderTask'))),
        this.createVertexTemplateEntry('markdown-document-reader', taskWidth, 40, createMarkdownDocumentReaderTask(mxResources.get('markdownDocumentReaderTask'))),
        this.createVertexTemplateEntry('yaml-document-reader', taskWidth, 40, createYamlDocumentReaderTask(mxResources.get('yamlDocumentReaderTask'))),
        this.createVertexTemplateEntry('ai-vector-store', taskWidth, 40, createAIVectorStoreTask(mxResources.get('aiVectorStoreTask'))),
        this.createVertexTemplateEntry('ai-vector-search', taskWidth, 40, createAIVectorSearchTask(mxResources.get('aiVectorSearchTask'))),
        this.createVertexTemplateEntry('ai-vector-delete', taskWidth, 40, createAIVectorDeleteTask(mxResources.get('aiVectorDeleteTask'))),
        this.createVertexTemplateEntry('jq', taskWidth, 40, createJQTask(mxResources.get('jq'))),
        this.createVertexTemplateEntry('wait', taskWidth, 40, createWaitTask(mxResources.get('wait'))),
        this.createVertexTemplateEntry('email', taskWidth, 40, createEmailTask(mxResources.get('emailTask'))),
        this.createVertexTemplateEntry('map', taskWidth, 40, createMapTask(mxResources.get('mapTask'))),
        this.createVertexTemplateEntry('end', 30, 30, createTerminateTask(mxResources.get('end'))),
        this.createVertexTemplateEntry('json-converter', taskWidth, 40, createJsonConverterTask(mxResources.get('jsonConverterTask')))
    ];

    this.addPaletteFunctions('workflow', 'Workflow', fns);
};

/**
 * Creates a thumbnail for the given cells.
 */
Palette.prototype.createThumb = function (cells, parent) {
    cells = this.graph.cloneCells(cells);
    for (const cell of cells) {
        if (cell.getValue()?.type === 'Terminate') {
            const geometry = cell.geometry.clone();
            geometry.width = 40;
            geometry.height = 40;
            cell.geometry = geometry;
        }
        const minimum = this.graph.getMinimumVertexSize(cell);
        if (minimum != null) {
            cell.geometry.width = this.graph.getPreferredSizeForCell(cell).width;
        }
    }
    this.graph.labelsVisible = true;
    const fo = mxClient.NO_FO;
    mxClient.NO_FO = Editor.prototype.originalNoForeignObject;
    this.graph.view.scaleAndTranslate(1, 0, 0);
    this.graph.addCells(cells);
    const bounds = this.graph.getGraphBounds();
    if (this.thumbScale == null) {
        this.thumbScale = this.thumbHeight / bounds.height;
    }
    const scale = this.thumbScale;
    this.graph.view.scaleAndTranslate(scale, 0, 0);
    this.container.appendChild(this.graph.container);
    this.graph.refresh();
    for (const cell of cells) {
        const state = this.graph.view.getState(cell);
        const textBounds = state?.text?.node.querySelector('text')?.getBoundingClientRect();
        const imageBounds = state?.shape?.node.querySelector('image')?.getBoundingClientRect();
        const shapeBounds = state?.shape?.node.querySelector('[pointer-events="all"]')?.getBoundingClientRect();
        if (textBounds != null && imageBounds != null && shapeBounds != null) {
            const contentBounds = new mxRectangle(textBounds.x, textBounds.y, textBounds.width, textBounds.height);
            contentBounds.add(new mxRectangle(imageBounds.x, imageBounds.y, imageBounds.width, imageBounds.height));
            const leftInset = contentBounds.x - shapeBounds.x;
            const geometry = cell.geometry.clone();
            geometry.width = (contentBounds.width + 2 * leftInset) / scale;
            this.graph.getModel().setGeometry(cell, geometry);
        }
    }
    const scaleAndTranslateBounds = this.graph.getGraphBounds();

    const node = this.graph.view.getCanvas().ownerSVGElement.cloneNode(true);

    this.graph.getModel().clear();
    this.graph.container.remove();
    mxClient.NO_FO = fo;

    node.style.position = 'relative';
    node.style.overflow = 'hidden';
    node.style.cursor = 'move';
    node.style.left = '0px';
    node.style.top = '0px';
    node.style.width = Math.floor(scaleAndTranslateBounds.width) + 1 + 'px';
    node.style.height = Math.floor(scaleAndTranslateBounds.height) + 1 + 'px';
    node.style.visibility = '';
    node.style.minWidth = '';
    node.style.minHeight = '';
    node.style.background = 'transparent none'

    parent.appendChild(node);

    return bounds;
};

/**
 * Creates and returns a new palette item for the given image.
 */
Palette.prototype.createItem = function (cells, width, height, allowCellsInserted) {
    const elt = document.createElement('div');
    elt.className = 'gePalettePreview';

    // Blocks default click action
    mxEvent.addListener(elt, 'click', function (evt) {
        mxEvent.consume(evt);
    });

    this.createThumb(cells, elt);
    const bounds = new mxRectangle(0, 0, width, height);

    if (cells.length > 1 || cells[0].vertex) {
        const ds = this.createDragSource(elt, this.createDropHandler(cells, true, allowCellsInserted,
            bounds), this.createDragPreview(width, height), cells, bounds);
        this.addClickHandler(elt, ds, cells);

        // Uses guides for vertices only if enabled in graph
        ds.isGuidesEnabled = mxUtils.bind(this, function () {
            return this.editorUi.editor.graph.graphHandler.guidesEnabled;
        });
    } else if (cells[0] != null && cells[0].edge) {
        const ds = this.createDragSource(elt, this.createDropHandler(cells, false, allowCellsInserted,
            bounds), this.createDragPreview(width, height), cells, bounds);
        this.addClickHandler(elt, ds, cells);
    }

    const menuItem = document.createElement('div');
    menuItem.className = 'item';
    menuItem.appendChild(elt);
    return menuItem;
};

/**
 * Creates a drop handler for inserting the given cells.
 */
Palette.prototype.updateShapes = function (source, targets) {
    const graph = this.editorUi.editor.graph;
    const sourceCellStyle = graph.getCellStyle(source);
    const result = [];

    graph.model.beginUpdate();
    try {
        const cellStyle = graph.getModel().getStyle(source);

        // Lists the styles to carry over from the existing shape
        const styles = ['shadow', 'dashed', 'dashPattern', 'fontFamily', 'fontSize', 'fontColor', 'align', 'startFill',
            'startSize', 'endFill', 'endSize', 'strokeColor', 'strokeWidth', 'fillColor', 'gradientColor',
            'html', 'part', 'noEdgeStyle', 'edgeStyle', 'elbow', 'childLayout', 'recursiveResize',
            'container', 'connectable'];

        for (let i = 0; i < targets.length; i++) {
            const targetCell = targets[i];

            if ((graph.getModel().isVertex(targetCell) === graph.getModel().isVertex(source)) ||
                (graph.getModel().isEdge(targetCell) === graph.getModel().isEdge(source))) {
                const state = graph.view.getState(targetCell);
                const style = (state != null) ? state.style : graph.getCellStyle(targets[i]);
                graph.getModel().setStyle(targetCell, cellStyle);

                // Removes all children of composite cells
                if (state != null && mxUtils.getValue(state.style, 'composite', '0') === '1') {
                    const childCount = graph.model.getChildCount(targetCell);

                    for (let j = childCount; j >= 0; j--) {
                        graph.model.remove(graph.model.getChildAt(targetCell, j));
                    }
                }

                if (style != null) {
                    // Replaces the participant style in the lifeline shape with the target shape
                    if (style[mxConstants.STYLE_SHAPE] === 'umlLifeline' &&
                        sourceCellStyle[mxConstants.STYLE_SHAPE] !== 'umlLifeline') {
                        graph.setCellStyles(mxConstants.STYLE_SHAPE, 'umlLifeline', [targetCell]);
                        graph.setCellStyles('participant', sourceCellStyle[mxConstants.STYLE_SHAPE], [targetCell]);
                    }

                    for (let j = 0; j < styles.length; j++) {
                        const value = style[styles[j]];

                        if (value != null) {
                            graph.setCellStyles(styles[j], value, [targetCell]);
                        }
                    }
                }

                result.push(targetCell);
            }
        }
    } finally {
        graph.model.endUpdate();
    }

    return result;
};

/**
 * Creates a drop handler for inserting the given cells.
 */
Palette.prototype.createDropHandler = function (cells, allowSplit, allowCellsInserted, bounds) {
    allowCellsInserted = (allowCellsInserted != null) ? allowCellsInserted : true;

    return mxUtils.bind(this, function (graph, evt, target, x, y, force) {
        let elt = (force) ? null : (mxEvent.isTouchEvent(evt) ?
            document.elementFromPoint(mxEvent.getClientX(evt), mxEvent.getClientY(evt)) :
            mxEvent.getSource(evt));

        while (elt != null && elt !== this.container) {
            elt = elt.parentNode;
        }

        if (elt == null && graph.isEnabled()) {
            cells = graph.getImportableCells(cells);

            if (cells.length > 0) {
                graph.stopEditing();

                // Holding alt while mouse is released ignores drop target
                const validDropTarget = (target != null && !mxEvent.isAltDown(evt)) ?
                    graph.isValidDropTarget(target, cells, evt) : false;
                let select = null;

                if (target != null && !validDropTarget) {
                    target = null;
                }

                if (!graph.isCellLocked(target || graph.getDefaultParent())) {
                    graph.model.beginUpdate();
                    try {
                        x = Math.round(x);
                        y = Math.round(y);

                        // Splits the target edge or inserts into target group
                        if (allowSplit && graph.isSplitTarget(target, cells, evt)) {
                            const clones = graph.cloneCells(cells);
                            graph.splitEdge(target, clones, null,
                                x - bounds.width / 2, y - bounds.height / 2);
                            select = clones;
                        } else if (cells.length > 0) {
                            select = graph.importCells(cells, x, y, target);
                        }

                        // Executes parent layout hooks for position/order
                        if (graph.layoutManager != null) {
                            const layout = graph.layoutManager.getLayout(target);

                            if (layout != null) {
                                const s = graph.view.scale;
                                const tr = graph.view.translate;
                                const tx = (x + tr.x) * s;
                                const ty = (y + tr.y) * s;

                                for (let i = 0; i < select.length; i++) {
                                    layout.moveCell(select[i], tx, ty);
                                }
                            }
                        }

                        if (allowCellsInserted) {
                            graph.fireEvent(new mxEventObject('cellsInserted', 'cells', select));
                        }
                    } finally {
                        graph.model.endUpdate();
                    }

                    if (select != null && select.length > 0) {
                        graph.scrollCellToVisible(select[0]);
                        graph.setSelectionCells(select);
                    }

                    if (evt != null && mxEvent.isMouseEvent(evt) && select != null && select.length === 1) {
                        window.setTimeout(function () {
                            graph.startEditing(select[0]);
                        }, 0);
                    }
                }
            }

            mxEvent.consume(evt);
        }
    });
};

/**
 * Creates and returns a preview element for the given width and height.
 */
Palette.prototype.createDragPreview = function (width, height) {
    const elt = document.createElement('div');
    elt.style.border = this.dragPreviewBorder;
    elt.style.width = width + 'px';
    elt.style.height = height + 'px';

    return elt;
};

/**
 * Creates a drag source for the given element.
 */
Palette.prototype.dropAndConnect = function (source, targets, direction, dropCellIndex, evt) {
    const geo = this.getDropAndConnectGeometry(source, targets[dropCellIndex], direction, targets);

    // Targets without the new edge for selection
    let tmp = [];

    if (geo != null) {
        const graph = this.editorUi.editor.graph;
        let editingCell = null;

        graph.model.beginUpdate();
        try {
            const sourceGeo = graph.getCellGeometry(source);
            let geo2 = graph.getCellGeometry(targets[dropCellIndex]);

            // Handles special case where target should be ignored for stack layouts
            const targetParent = graph.model.getParent(source);
            let validLayout = true;

            // Ignores parent if it has a stack layout
            if (graph.layoutManager != null) {
                const layout = graph.layoutManager.getLayout(targetParent);

                // LATER: Use parent of parent if valid layout
                if (layout != null && layout.constructor === mxStackLayout) {
                    validLayout = false;

                    const tmp = graph.view.getState(targetParent);

                    // Offsets by parent position
                    if (tmp != null) {
                        const offset = new mxPoint((tmp.x / graph.view.scale - graph.view.translate.x),
                            (tmp.y / graph.view.scale - graph.view.translate.y));
                        geo.x += offset.x;
                        geo.y += offset.y;
                        const pt = geo.getTerminalPoint(false);

                        if (pt != null) {
                            pt.x += offset.x;
                            pt.y += offset.y;
                        }
                    }
                }
            }

            let dx = geo2.x;
            let dy = geo2.y;

            // Ignores geometry of edges
            if (graph.model.isEdge(targets[dropCellIndex])) {
                dx = 0;
                dy = 0;
            }

            const useParent = graph.model.isEdge(source) || (sourceGeo != null && !sourceGeo.relative && validLayout);
            targets = graph.importCells(targets, (geo.x - (useParent ? dx : 0)),
                (geo.y - (useParent ? dy : 0)), (useParent) ? targetParent : null);
            tmp = targets;

            if (graph.model.isEdge(source)) {
                // Adds new terminal to edge
                // LATER: Push new terminal out radially from edge start point
                graph.model.setTerminal(source, targets[dropCellIndex], direction === mxConstants.DIRECTION_NORTH);
            } else if (graph.model.isEdge(targets[dropCellIndex])) {
                // Adds new outgoing connection to vertex and clears points
                graph.model.setTerminal(targets[dropCellIndex], source, true);
                const geo3 = graph.getCellGeometry(targets[dropCellIndex]);
                geo3.points = null;

                if (geo3.getTerminalPoint(false) != null) {
                    geo3.setTerminalPoint(geo.getTerminalPoint(false), false);
                } else if (useParent && graph.model.isVertex(targetParent)) {
                    // Adds parent offset to other nodes
                    const tmpState = graph.view.getState(targetParent);
                    const offset = (tmpState.cell !== graph.view.currentRoot) ?
                        new mxPoint((tmpState.x / graph.view.scale - graph.view.translate.x),
                            (tmpState.y / graph.view.scale - graph.view.translate.y)) : new mxPoint(0, 0);

                    graph.cellsMoved(targets, offset.x, offset.y, null, null, true);
                }
            } else {
                geo2 = graph.getCellGeometry(targets[dropCellIndex]);
                dx = geo.x - Math.round(geo2.x);
                dy = geo.y - Math.round(geo2.y);
                geo.x = Math.round(geo2.x);
                geo.y = Math.round(geo2.y);
                graph.model.setGeometry(targets[dropCellIndex], geo);
                graph.cellsMoved(targets, dx, dy, null, null, true);
                tmp = targets.slice();
                editingCell = (tmp.length === 1) ? tmp[0] : null;
                targets.push(graph.insertEdge(null, null, '', source, targets[dropCellIndex],
                    graph.createCurrentEdgeStyle()));
            }

            graph.fireEvent(new mxEventObject('cellsInserted', 'cells', targets));
        } finally {
            graph.model.endUpdate();
        }

        if (evt != null && mxEvent.isMouseEvent(evt) && editingCell != null) {
            window.setTimeout(function () {
                graph.startEditing(editingCell);
            }, 0);
        }
    }

    return tmp;
};

/**
 * Creates a drag source for the given element.
 */
Palette.prototype.getDropAndConnectGeometry = function (source, target, direction, targets) {
    const graph = this.editorUi.editor.graph;
    const view = graph.view;
    const keepSize = targets.length > 1;
    let geo = graph.getCellGeometry(source);
    let geo2 = graph.getCellGeometry(target);

    if (geo != null && geo2 != null) {
        geo2 = geo2.clone();

        if (graph.model.isEdge(source)) {
            const state = graph.view.getState(source);
            const pts = state.absolutePoints;
            const p0 = pts[0];
            const pe = pts[pts.length - 1];

            if (direction === mxConstants.DIRECTION_NORTH) {
                geo2.x = p0.x / view.scale - view.translate.x - geo2.width / 2;
                geo2.y = p0.y / view.scale - view.translate.y - geo2.height / 2;
            } else {
                geo2.x = pe.x / view.scale - view.translate.x - geo2.width / 2;
                geo2.y = pe.y / view.scale - view.translate.y - geo2.height / 2;
            }
        } else {
            if (geo.relative) {
                const state = graph.view.getState(source);
                geo = geo.clone();
                geo.x = (state.x - view.translate.x) / view.scale;
                geo.y = (state.y - view.translate.y) / view.scale;
            }

            let length = graph.defaultEdgeLength;

            // Maintains edge length
            if (graph.model.isEdge(target) && geo2.getTerminalPoint(true) != null && geo2.getTerminalPoint(false) != null) {
                const p0 = geo2.getTerminalPoint(true);
                const pe = geo2.getTerminalPoint(false);
                const dx = pe.x - p0.x;
                const dy = pe.y - p0.y;

                length = Math.sqrt(dx * dx + dy * dy);

                geo2.x = geo.getCenterX();
                geo2.y = geo.getCenterY();
                geo2.width = 1;
                geo2.height = 1;

                if (direction === mxConstants.DIRECTION_NORTH) {
                    geo2.height = length;
                    geo2.y = geo.y - length;
                    geo2.setTerminalPoint(new mxPoint(geo2.x, geo2.y), false);
                } else if (direction === mxConstants.DIRECTION_EAST) {
                    geo2.width = length;
                    geo2.x = geo.x + geo.width;
                    geo2.setTerminalPoint(new mxPoint(geo2.x + geo2.width, geo2.y), false);
                } else if (direction === mxConstants.DIRECTION_SOUTH) {
                    geo2.height = length;
                    geo2.y = geo.y + geo.height;
                    geo2.setTerminalPoint(new mxPoint(geo2.x, geo2.y + geo2.height), false);
                } else if (direction === mxConstants.DIRECTION_WEST) {
                    geo2.width = length;
                    geo2.x = geo.x - length;
                    geo2.setTerminalPoint(new mxPoint(geo2.x, geo2.y), false);
                }
            } else {
                // Try match size or ignore if width or height < 45 which
                // is considered special enough to be ignored here
                if (!keepSize && geo2.width > 45 && geo2.height > 45 &&
                    geo.width > 45 && geo.height > 45) {
                    geo2.width = geo2.width * (geo.height / geo2.height);
                    geo2.height = geo.height;
                }

                geo2.x = geo.x + geo.width / 2 - geo2.width / 2;
                geo2.y = geo.y + geo.height / 2 - geo2.height / 2;

                if (direction === mxConstants.DIRECTION_NORTH) {
                    geo2.y = geo2.y - geo.height / 2 - geo2.height / 2 - length;
                } else if (direction === mxConstants.DIRECTION_EAST) {
                    geo2.x = geo2.x + geo.width / 2 + geo2.width / 2 + length;
                } else if (direction === mxConstants.DIRECTION_SOUTH) {
                    geo2.y = geo2.y + geo.height / 2 + geo2.height / 2 + length;
                } else if (direction === mxConstants.DIRECTION_WEST) {
                    geo2.x = geo2.x - geo.width / 2 - geo2.width / 2 - length;
                }

                // Adds offset to match cells without connecting edge
                if (graph.model.isEdge(target) && geo2.getTerminalPoint(true) != null && target.getTerminal(false) != null) {
                    const targetGeo = graph.getCellGeometry(target.getTerminal(false));

                    if (targetGeo != null) {
                        if (direction === mxConstants.DIRECTION_NORTH) {
                            geo2.x -= targetGeo.getCenterX();
                            geo2.y -= targetGeo.getCenterY() + targetGeo.height / 2;
                        } else if (direction === mxConstants.DIRECTION_EAST) {
                            geo2.x -= targetGeo.getCenterX() - targetGeo.width / 2;
                            geo2.y -= targetGeo.getCenterY();
                        } else if (direction === mxConstants.DIRECTION_SOUTH) {
                            geo2.x -= targetGeo.getCenterX();
                            geo2.y -= targetGeo.getCenterY() - targetGeo.height / 2;
                        } else if (direction === mxConstants.DIRECTION_WEST) {
                            geo2.x -= targetGeo.getCenterX() + targetGeo.width / 2;
                            geo2.y -= targetGeo.getCenterY();
                        }
                    }
                }
            }
        }
    }

    return geo2;
};

/**
 * Creates a drag source for the given element.
 */
Palette.prototype.createDragSource = function (elt, dropHandler, preview, cells, bounds) {
    // Checks if the cells contain any vertices
    const ui = this.editorUi;
    const graph = ui.editor.graph;
    let freeSourceEdge = null;
    let firstVertex = null;
    const pallete = this;

    for (let i = 0; i < cells.length; i++) {
        if (firstVertex == null && this.editorUi.editor.graph.model.isVertex(cells[i])) {
            firstVertex = i;
        } else if (freeSourceEdge == null && this.editorUi.editor.graph.model.isEdge(cells[i]) &&
            this.editorUi.editor.graph.model.getTerminal(cells[i], true) == null) {
            freeSourceEdge = i;
        }

        if (firstVertex != null && freeSourceEdge != null) {
            break;
        }
    }

    const dragSource = mxUtils.makeDraggable(elt, this.editorUi.editor.graph, mxUtils.bind(this, function (graph, evt, target, x, y) {
        if (this.updateThread != null) {
            window.clearTimeout(this.updateThread);
        }

        if (cells != null && currentStyleTarget != null && activeArrow === styleTarget) {
            const tmp = graph.isCellSelected(currentStyleTarget.cell) ? graph.getSelectionCells() : [currentStyleTarget.cell];
            const updatedCells = this.updateShapes((graph.model.isEdge(currentStyleTarget.cell)) ? cells[0] : cells[firstVertex], tmp);
            graph.setSelectionCells(updatedCells);
        } else if (cells != null && activeArrow != null && currentTargetState != null && activeArrow !== styleTarget) {
            const index = (graph.model.isEdge(currentTargetState.cell) || freeSourceEdge == null) ? firstVertex : freeSourceEdge;
            graph.setSelectionCells(this.dropAndConnect(currentTargetState.cell, cells, direction, index, evt));
        } else {
            dropHandler.apply(this, arguments);
        }

        if (this.editorUi.hoverIcons != null) {
            this.editorUi.hoverIcons.update(graph.view.getState(graph.getSelectionCell()));
        }
    }), null, 0, 0, graph.autoscroll, true, true);
    dragSource.dragElementOpacity = 0;
    dragSource.createDragElement = function () {
        const dragElement = document.createElement('div');
        dragElement.className = suppressedDragElementClassName;
        dragElement.style.display = 'none';
        dragElement.style.height = '1px';
        dragElement.style.pointerEvents = 'none';
        dragElement.style.width = '1px';
        return dragElement;
    };

    // Stops dragging if cancel is pressed
    graph.addListener(mxEvent.ESCAPE, function () {
        if (dragSource.isActive()) {
            dragSource.reset();
        }
    });

    // Overrides mouseDown to ignore popup triggers
    const mouseDown = dragSource.mouseDown;

    dragSource.mouseDown = function (evt) {
        if (!mxEvent.isPopupTrigger(evt) && !mxEvent.isMultiTouchEvent(evt)) {
            graph.stopEditing();
            mouseDown.apply(this, arguments);
        }
    };

    // Workaround for event redirection via image tag in quirks and IE8
    function createArrow(img, tooltip) {
        const arrow = mxUtils.createImage(img.src);
        arrow.style.width = img.width + 'px';
        arrow.style.height = img.height + 'px';

        if (tooltip != null) {
            arrow.setAttribute('title', tooltip);
        }

        mxUtils.setOpacity(arrow, (img === Palette.prototype.refreshTarget) ? 30 : 20);
        arrow.style.position = 'absolute';
        arrow.style.cursor = 'crosshair';

        return arrow;
    }

    let currentTargetState = null;
    let currentStateHandle = null;
    let currentStyleTarget = null;
    let activeTarget = false;

    const arrowUp = createArrow(this.triangleUp, mxResources.get('connect'));
    const arrowRight = createArrow(this.triangleRight, mxResources.get('connect'));
    const arrowDown = createArrow(this.triangleDown, mxResources.get('connect'));
    const arrowLeft = createArrow(this.triangleLeft, mxResources.get('connect'));
    const styleTarget = createArrow(this.refreshTarget, mxResources.get('replace'));
    // Workaround for actual parentNode not being updated in old IE
    let styleTargetParent = null;
    const roundSource = createArrow(this.roundDrop);
    const roundTarget = createArrow(this.roundDrop);
    let direction = mxConstants.DIRECTION_NORTH;
    let activeArrow = null;

    function checkArrow(x, y, bounds, arrow) {
        if (arrow.parentNode != null) {
            if (mxUtils.contains(bounds, x, y)) {
                mxUtils.setOpacity(arrow, 100);
                activeArrow = arrow;
            } else {
                mxUtils.setOpacity(arrow, (arrow === styleTarget) ? 30 : 20);
            }
        }

        return bounds;
    }

    // Hides guides and preview if target is active
    const dsCreatePreviewElement = dragSource.createPreviewElement;

    // Stores initial size of preview element
    dragSource.createPreviewElement = function (graph) {
        let elt = dsCreatePreviewElement.apply(this, arguments);

        if (elt == null && preview != null) {
            elt = preview.cloneNode(true);
        }

        if (elt == null) {
            elt = pallete.createDragPreview(bounds.width, bounds.height);
        }

        // Pass-through events required to tooltip on replace shape
        if (mxClient.IS_SVG) {
            elt.style.pointerEvents = 'none';
        }

        this.previewElementWidth = elt.style.width;
        this.previewElementHeight = elt.style.height;

        return elt;
    };

    // Shows/hides hover icons
    const dragEnter = dragSource.dragEnter;
    dragSource.dragEnter = function (graph, evt) {
        if (ui.hoverIcons != null) {
            ui.hoverIcons.setDisplay('none');
        }

        dragEnter.apply(this, arguments);
    };

    const dragExit = dragSource.dragExit;
    dragSource.dragExit = function (graph, evt) {
        if (ui.hoverIcons != null) {
            ui.hoverIcons.setDisplay('');
        }

        dragExit.apply(this, arguments);
    };

    dragSource.dragOver = function (graph, evt) {
        mxDragSource.prototype.dragOver.apply(this, arguments);

        if (this.currentGuide != null && activeArrow != null) {
            this.currentGuide.hide();
        }

        if (this.previewElement != null) {
            const view = graph.view;

            if (currentStyleTarget != null && activeArrow === styleTarget) {
                this.previewElement.style.display = (graph.model.isEdge(currentStyleTarget.cell)) ? 'none' : '';

                this.previewElement.style.left = currentStyleTarget.x + 'px';
                this.previewElement.style.top = currentStyleTarget.y + 'px';
                this.previewElement.style.width = currentStyleTarget.width + 'px';
                this.previewElement.style.height = currentStyleTarget.height + 'px';
            } else if (currentTargetState != null && activeArrow != null) {
                const index = (graph.model.isEdge(currentTargetState.cell) || freeSourceEdge == null) ? firstVertex : freeSourceEdge;
                const geo = pallete.getDropAndConnectGeometry(currentTargetState.cell, cells[index], direction, cells);
                const geo2 = (!graph.model.isEdge(currentTargetState.cell)) ? graph.getCellGeometry(currentTargetState.cell) : null;
                const geo3 = graph.getCellGeometry(cells[index]);
                const parent = graph.model.getParent(currentTargetState.cell);
                let dx = view.translate.x * view.scale;
                let dy = view.translate.y * view.scale;

                if (geo2 != null && !geo2.relative && graph.model.isVertex(parent) && parent !== view.currentRoot) {
                    const pState = view.getState(parent);

                    dx = pState.x;
                    dy = pState.y;
                }

                let dx2 = geo3.x;
                let dy2 = geo3.y;

                // Ignores geometry of edges
                if (graph.model.isEdge(cells[index])) {
                    dx2 = 0;
                    dy2 = 0;
                }

                // Shows preview at drop location
                this.previewElement.style.left = ((geo.x - dx2) * view.scale + dx) + 'px';
                this.previewElement.style.top = ((geo.y - dy2) * view.scale + dy) + 'px';

                if (cells.length === 1) {
                    this.previewElement.style.width = (geo.width * view.scale) + 'px';
                    this.previewElement.style.height = (geo.height * view.scale) + 'px';
                }

                this.previewElement.style.display = '';
            } else if (dragSource.currentHighlight.state != null &&
                graph.model.isEdge(dragSource.currentHighlight.state.cell)) {
                // Centers drop cells when splitting edges
                this.previewElement.style.left = Math.round(parseInt(this.previewElement.style.left, 10) -
                    bounds.width * view.scale / 2) + 'px';
                this.previewElement.style.top = Math.round(parseInt(this.previewElement.style.top, 10) -
                    bounds.height * view.scale / 2) + 'px';
            } else {
                this.previewElement.style.width = this.previewElementWidth;
                this.previewElement.style.height = this.previewElementHeight;
                this.previewElement.style.display = '';
            }
        }
    };

    let startTime = new Date().getTime();
    let timeOnTarget = 0;
    let prev = null;

    // Gets source cell style to compare shape below
    const sourceCellStyle = this.editorUi.editor.graph.getCellStyle(cells[0]);

    // Allows drop into cell only if target is a valid root
    dragSource.getDropTarget = mxUtils.bind(this, function (graph, x, y, evt) {
        // Alt means no targets at all
        // LATER: Show preview where result will go
        let cell = (!mxEvent.isAltDown(evt) && cells != null) ? graph.getCellAt(x, y) : null;

        if (cell != null && (cell.isStart() || cell.isEnd())) {
            return;
        }

        // Uses connectable parent vertex if one exists
        if (cell != null && !this.graph.isCellConnectable(cell)) {
            const parent = this.graph.getModel().getParent(cell);

            if (this.graph.getModel().isVertex(parent) && this.graph.isCellConnectable(parent)) {
                cell = parent;
            }
        }

        // Ignores locked cells
        if (graph.isCellLocked(cell)) {
            cell = null;
        }

        let state = graph.view.getState(cell);
        activeArrow = null;
        let bbox = null;

        // Time on target
        if (prev !== state) {
            prev = state;
            startTime = new Date().getTime();
            timeOnTarget = 0;

            if (this.updateThread != null) {
                window.clearTimeout(this.updateThread);
            }

            if (state != null) {
                this.updateThread = window.setTimeout(function () {
                    if (activeArrow == null) {
                        prev = state;
                        dragSource.getDropTarget(graph, x, y, evt);
                    }
                }, this.dropTargetDelay + 10);
            }
        } else {
            timeOnTarget = new Date().getTime() - startTime;
        }

        // Shift means disabled, delayed on cells with children, shows after this.dropTargetDelay, hides after 2500ms
        if (timeOnTarget < 3500 && state != null && !mxEvent.isShiftDown(evt) &&
            // If shape is equal or target has no stroke, fill and gradient then use longer delay except for images
            (((mxUtils.getValue(state.style, mxConstants.STYLE_SHAPE) !== mxUtils.getValue(sourceCellStyle, mxConstants.STYLE_SHAPE) &&
                        (mxUtils.getValue(state.style, mxConstants.STYLE_STROKECOLOR, mxConstants.NONE) !== mxConstants.NONE ||
                            mxUtils.getValue(state.style, mxConstants.STYLE_FILLCOLOR, mxConstants.NONE) !== mxConstants.NONE ||
                            mxUtils.getValue(state.style, mxConstants.STYLE_GRADIENTCOLOR, mxConstants.NONE) !== mxConstants.NONE)) ||
                    mxUtils.getValue(sourceCellStyle, mxConstants.STYLE_SHAPE) === 'image') ||
                timeOnTarget > 500 || graph.model.isEdge(state.cell)) && (timeOnTarget > this.dropTargetDelay) &&
            ((graph.model.isVertex(state.cell) && firstVertex != null) ||
                (graph.model.isEdge(state.cell) && graph.model.isEdge(cells[0])))) {
            currentStyleTarget = state;
            const geometry = graph.getCellGeometry(cell);
            let tmp = (graph.model.isEdge(state.cell)) ? graph.view.getPoint(state) : new mxPoint(state.getCenterX(), state.getCenterY());
            tmp = new mxRectangle(tmp.x - this.refreshTarget.width / 2, tmp.y - this.refreshTarget.height / 2, geometry.width, geometry.height);

            styleTarget.style.left = Math.floor(tmp.x) + 'px';
            styleTarget.style.top = Math.floor(tmp.y) + 'px';

            if (styleTargetParent == null) {
                graph.container.appendChild(styleTarget);
                styleTargetParent = styleTarget.parentNode;
            }

            checkArrow(x, y, tmp, styleTarget);
        }
        // Does not reset on ignored edges
        else if (currentStyleTarget == null || !mxUtils.contains(currentStyleTarget, x, y) ||
            (timeOnTarget > 500 && !mxEvent.isShiftDown(evt))) {
            currentStyleTarget = null;

            if (styleTargetParent != null) {
                styleTarget.parentNode.removeChild(styleTarget);
                styleTargetParent = null;
            }
        } else if (currentStyleTarget != null && styleTargetParent != null) {
            // Sets active Arrow as side effect
            let tmp = (graph.model.isEdge(currentStyleTarget.cell)) ? graph.view.getPoint(currentStyleTarget) : new mxPoint(currentStyleTarget.getCenterX(), currentStyleTarget.getCenterY());
            tmp = new mxRectangle(tmp.x - this.refreshTarget.width / 2, tmp.y - this.refreshTarget.height / 2,
                this.refreshTarget.width, this.refreshTarget.height);
            checkArrow(x, y, tmp, styleTarget);
        }

        // Checks if inside bounds
        if (activeTarget && currentTargetState != null && !mxEvent.isAltDown(evt) && activeArrow == null) {
            // LATER: Use hit-detection for edges
            bbox = mxRectangle.fromRectangle(currentTargetState);

            if (graph.model.isEdge(currentTargetState.cell)) {
                const pts = currentTargetState.absolutePoints;

                if (roundSource.parentNode != null) {
                    const p0 = pts[0];
                    bbox.add(checkArrow(x, y, new mxRectangle(p0.x - this.roundDrop.width / 2,
                        p0.y - this.roundDrop.height / 2, this.roundDrop.width, this.roundDrop.height), roundSource));
                }

                if (roundTarget.parentNode != null) {
                    const pe = pts[pts.length - 1];
                    bbox.add(checkArrow(x, y, new mxRectangle(pe.x - this.roundDrop.width / 2,
                        pe.y - this.roundDrop.height / 2,
                        this.roundDrop.width, this.roundDrop.height), roundTarget));
                }
            } else {
                let bds = mxRectangle.fromRectangle(currentTargetState);

                // Uses outer bounding box to take rotation into account
                if (currentTargetState.shape != null && currentTargetState.shape.boundingBox != null) {
                    bds = mxRectangle.fromRectangle(currentTargetState.shape.boundingBox);
                }

                bds.grow(this.graph.tolerance);
                bds.grow(HoverIcons.prototype.arrowSpacing);

                const handler = this.graph.selectionCellsHandler.getHandler(currentTargetState.cell);

                if (handler != null) {
                    bds.x -= handler.horizontalOffset / 2;
                    bds.y -= handler.verticalOffset / 2;
                    bds.width += handler.horizontalOffset;
                    bds.height += handler.verticalOffset;

                    // Adds bounding box of rotation handle to avoid overlap
                    if (handler.rotationShape != null && handler.rotationShape.node != null &&
                        handler.rotationShape.node.style.visibility !== 'hidden' &&
                        handler.rotationShape.node.style.display !== 'none' &&
                        handler.rotationShape.boundingBox != null) {
                        bds.add(handler.rotationShape.boundingBox);
                    }
                }

                bbox.add(checkArrow(x, y, new mxRectangle(currentTargetState.getCenterX() - this.triangleUp.width / 2,
                    bds.y - this.triangleUp.height, this.triangleUp.width, this.triangleUp.height), arrowUp));
                bbox.add(checkArrow(x, y, new mxRectangle(bds.x + bds.width,
                    currentTargetState.getCenterY() - this.triangleRight.height / 2,
                    this.triangleRight.width, this.triangleRight.height), arrowRight));
                bbox.add(checkArrow(x, y, new mxRectangle(currentTargetState.getCenterX() - this.triangleDown.width / 2,
                    bds.y + bds.height, this.triangleDown.width, this.triangleDown.height), arrowDown));
                bbox.add(checkArrow(x, y, new mxRectangle(bds.x - this.triangleLeft.width,
                    currentTargetState.getCenterY() - this.triangleLeft.height / 2,
                    this.triangleLeft.width, this.triangleLeft.height), arrowLeft));
            }

            // Adds tolerance
            if (bbox != null) {
                bbox.grow(10);
            }
        }

        direction = mxConstants.DIRECTION_NORTH;

        if (activeArrow === arrowRight) {
            direction = mxConstants.DIRECTION_EAST;
        } else if (activeArrow === arrowDown || activeArrow === roundTarget) {
            direction = mxConstants.DIRECTION_SOUTH;
        } else if (activeArrow === arrowLeft) {
            direction = mxConstants.DIRECTION_WEST;
        }

        if (currentStyleTarget != null && activeArrow === styleTarget) {
            state = currentStyleTarget;
        }

        const validTarget = (firstVertex == null || graph.isCellConnectable(cells[firstVertex])) &&
            ((graph.model.isEdge(cell) && firstVertex != null) ||
                (graph.model.isVertex(cell) && graph.isCellConnectable(cell)));

        // Drop arrows shown after this.dropTargetDelay, hidden after 5 secs, switches arrows after 500ms
        if ((currentTargetState != null && timeOnTarget >= 5000) ||
            (currentTargetState !== state &&
                (bbox == null || !mxUtils.contains(bbox, x, y) ||
                    (timeOnTarget > 500 && activeArrow == null && validTarget)))) {
            activeTarget = false;
            currentTargetState = ((timeOnTarget < 5000 && timeOnTarget > this.dropTargetDelay) || graph.model.isEdge(cell)) ? state : null;

            if (currentTargetState != null && validTarget) {
                const elts = [roundSource, roundTarget, arrowUp, arrowRight, arrowDown, arrowLeft];

                for (let i = 0; i < elts.length; i++) {
                    if (elts[i].parentNode != null) {
                        elts[i].parentNode.removeChild(elts[i]);
                    }
                }

                if (graph.model.isEdge(cell)) {
                    const pts = state.absolutePoints;

                    if (pts != null) {
                        const p0 = pts[0];
                        const pe = pts[pts.length - 1];

                        roundSource.style.left = Math.floor(p0.x - this.roundDrop.width / 2) + 'px';
                        roundSource.style.top = Math.floor(p0.y - this.roundDrop.height / 2) + 'px';

                        roundTarget.style.left = Math.floor(pe.x - this.roundDrop.width / 2) + 'px';
                        roundTarget.style.top = Math.floor(pe.y - this.roundDrop.height / 2) + 'px';

                        if (graph.model.getTerminal(cell, true) == null) {
                            graph.container.appendChild(roundSource);
                        }

                        if (graph.model.getTerminal(cell, false) == null) {
                            graph.container.appendChild(roundTarget);
                        }
                    }
                } else {
                    let bds = mxRectangle.fromRectangle(state);

                    // Uses outer bounding box to take rotation into account
                    if (state.shape != null && state.shape.boundingBox != null) {
                        bds = mxRectangle.fromRectangle(state.shape.boundingBox);
                    }

                    bds.grow(this.graph.tolerance);
                    bds.grow(HoverIcons.prototype.arrowSpacing);

                    const handler = this.graph.selectionCellsHandler.getHandler(state.cell);

                    if (handler != null) {
                        bds.x -= handler.horizontalOffset / 2;
                        bds.y -= handler.verticalOffset / 2;
                        bds.width += handler.horizontalOffset;
                        bds.height += handler.verticalOffset;

                        // Adds bounding box of rotation handle to avoid overlap
                        if (handler.rotationShape != null && handler.rotationShape.node != null &&
                            handler.rotationShape.node.style.visibility !== 'hidden' &&
                            handler.rotationShape.node.style.display !== 'none' &&
                            handler.rotationShape.boundingBox != null) {
                            bds.add(handler.rotationShape.boundingBox);
                        }
                    }

                    arrowUp.style.left = Math.floor(state.getCenterX() - this.triangleUp.width / 2) + 'px';
                    arrowUp.style.top = Math.floor(bds.y - this.triangleUp.height) + 'px';

                    arrowRight.style.left = Math.floor(bds.x + bds.width) + 'px';
                    arrowRight.style.top = Math.floor(state.getCenterY() - this.triangleRight.height / 2) + 'px';

                    arrowDown.style.left = arrowUp.style.left
                    arrowDown.style.top = Math.floor(bds.y + bds.height) + 'px';

                    arrowLeft.style.left = Math.floor(bds.x - this.triangleLeft.width) + 'px';
                    arrowLeft.style.top = arrowRight.style.top;

                    if (state.style['portConstraint'] !== 'eastwest') {
                        graph.container.appendChild(arrowUp);
                        graph.container.appendChild(arrowDown);
                    }

                    graph.container.appendChild(arrowRight);
                    graph.container.appendChild(arrowLeft);
                }

                // Hides handle for cell under mouse
                if (state != null) {
                    currentStateHandle = graph.selectionCellsHandler.getHandler(state.cell);

                    if (currentStateHandle != null && currentStateHandle.setHandlesVisible != null) {
                        currentStateHandle.setHandlesVisible(false);
                    }
                }

                activeTarget = true;
            } else {
                const elts = [roundSource, roundTarget, arrowUp, arrowRight, arrowDown, arrowLeft];

                for (let i = 0; i < elts.length; i++) {
                    if (elts[i].parentNode != null) {
                        elts[i].parentNode.removeChild(elts[i]);
                    }
                }
            }
        }

        if (!activeTarget && currentStateHandle != null) {
            currentStateHandle.setHandlesVisible(true);
        }

        // Handles drop target
        let target = ((!mxEvent.isAltDown(evt) || mxEvent.isShiftDown(evt)) &&
            !(currentStyleTarget != null && activeArrow === styleTarget)) ?
            mxDragSource.prototype.getDropTarget.apply(this, arguments) : null;
        const model = graph.getModel();

        if (target != null) {
            if (activeArrow != null || !graph.isSplitTarget(target, cells, evt)) {
                // Selects parent group as drop target
                while (target != null && !graph.isValidDropTarget(target, cells, evt) && model.isVertex(model.getParent(target))) {
                    target = model.getParent(target);
                }

                if (graph.view.currentRoot === target || graph.getModel().getChildCount(target) === 0 || graph.isCellLocked(target) || model.isEdge(target)) {
                    target = null;
                }
            }
        }

        return target;
    });

    dragSource.stopDrag = function () {
        mxDragSource.prototype.stopDrag.apply(this, arguments);

        const elts = [roundSource, roundTarget, styleTarget, arrowUp, arrowRight, arrowDown, arrowLeft];

        for (let i = 0; i < elts.length; i++) {
            if (elts[i].parentNode != null) {
                elts[i].parentNode.removeChild(elts[i]);
            }
        }

        if (currentTargetState != null && currentStateHandle != null) {
            currentStateHandle.reset();
        }

        currentStateHandle = null;
        currentTargetState = null;
        currentStyleTarget = null;
        styleTargetParent = null;
        activeArrow = null;
    };

    return dragSource;
};

/**
 * Adds a handler for inserting the cell with a single click.
 */
Palette.prototype.itemClicked = function (cells, ds, evt) {
    const graph = this.editorUi.editor.graph;
    graph.container.focus();

    // Alt+Click inserts and connects
    if (mxEvent.isAltDown(evt)) {
        if (graph.getSelectionCount() === 1 && graph.model.isVertex(graph.getSelectionCell())) {
            let firstVertex = null;

            for (let i = 0; i < cells.length && firstVertex == null; i++) {
                if (graph.model.isVertex(cells[i])) {
                    firstVertex = i;
                }
            }

            if (firstVertex != null) {
                graph.setSelectionCells(this.dropAndConnect(graph.getSelectionCell(), cells, (mxEvent.isMetaDown(evt) || mxEvent.isControlDown(evt)) ?
                        (mxEvent.isShiftDown(evt) ? mxConstants.DIRECTION_WEST : mxConstants.DIRECTION_NORTH) :
                        (mxEvent.isShiftDown(evt) ? mxConstants.DIRECTION_EAST : mxConstants.DIRECTION_SOUTH),
                    firstVertex, evt));
                graph.scrollCellToVisible(graph.getSelectionCell());
            }
        }
    }
    // Shift+Click updates shape
    else if (mxEvent.isShiftDown(evt) && !graph.isSelectionEmpty()) {
        this.updateShapes(cells[0], graph.getSelectionCells());
        graph.scrollCellToVisible(graph.getSelectionCell());
    } else {
        const pt = graph.getFreeInsertPoint();
        ds.drop(graph, evt, null, pt.x, pt.y, true);

        if (this.editorUi.hoverIcons != null && mxEvent.isTouchEvent(evt)) {
            this.editorUi.hoverIcons.update(graph.view.getState(graph.getSelectionCell()));
        }
    }
};

/**
 * Adds a handler for inserting the cell with a single click.
 */
Palette.prototype.addClickHandler = function (elt, ds, cells) {
    const graph = this.editorUi.editor.graph;
    const oldMouseDown = ds.mouseDown;
    const oldMouseMove = ds.mouseMove;
    const oldMouseUp = ds.mouseUp;
    const tol = graph.tolerance;
    let first = null;
    let clicked = false;
    const sb = this;

    ds.mouseDown = function (evt) {
        oldMouseDown.apply(this, arguments);
        first = new mxPoint(mxEvent.getClientX(evt), mxEvent.getClientY(evt));

        if (this.dragElement != null) {
            this.dragElement.style.display = 'none';
            mxUtils.setOpacity(elt, 50);
        }
    };

    ds.mouseMove = function (evt) {
        if (this.dragElement != null && this.dragElement.style.display === 'none' &&
            first != null && (Math.abs(first.x - mxEvent.getClientX(evt)) > tol ||
                Math.abs(first.y - mxEvent.getClientY(evt)) > tol)) {
            this.dragElement.style.display = '';
            mxUtils.setOpacity(elt, 100);
        }

        oldMouseMove.apply(this, arguments);
    };

    ds.mouseUp = function (evt) {
        if (!mxEvent.isPopupTrigger(evt) && this.currentGraph == null &&
            this.dragElement != null && this.dragElement.style.display === 'none') {
            clicked = true;
            sb.itemClicked(cells, ds, evt, elt);
        }

        oldMouseUp.apply(ds, arguments);
        mxUtils.setOpacity(elt, 100);
        first = null;

        // Blocks tooltips on this element after single click
        sb.currentElt = elt;
    };

    mxEvent.addListener(elt, 'click', function (evt) {
        if (!mxEvent.isPopupTrigger(evt) && !clicked && !ds.isActive()) {
            sb.itemClicked(cells, ds, evt, elt);
        }

        clicked = false;
    });
};

/**
 * Creates a drop handler for inserting the given cells.
 */
Palette.prototype.createVertexTemplateEntry = function (style, width, height, value) {
    return mxUtils.bind(this, function () {
        return this.createVertexTemplate(style, width, height, value);
    });
};

/**
 * Creates a drop handler for inserting the given cells.
 */
Palette.prototype.createVertexTemplate = function (style, width, height, value, allowCellsInserted) {
    const cell = new mxCell((value != null) ? value : '', new mxGeometry(0, 0, width, height), style);
    cell.vertex = true;

    const minimum = this.graph.getMinimumVertexSize(cell);
    if (minimum != null) {
        width = minimum.width;
        cell.geometry.width = width;
    }

    return this.createVertexTemplateFromCells([cell], width, height, allowCellsInserted);
};

/**
 * Creates a drop handler for inserting the given cells.
 */
Palette.prototype.createVertexTemplateFromCells = function (cells, width, height, allowCellsInserted) {
    return this.createItem(cells, width, height, allowCellsInserted);
};

/**
 * Adds the given palette.
 */
Palette.prototype.addPaletteFunctions = function (id, title, fns) {
    this.addPalette(id, title, mxUtils.bind(this, function (content) {
        for (let i = 0; i < fns.length; i++) {
            content.appendChild(fns[i](content));
        }
        [...content.children]
            .sort((left, right) => {
                const leftLabel = left.textContent.trim();
                const rightLabel = right.textContent.trim();
                return Number(leftLabel === mxResources.get('end')) - Number(rightLabel === mxResources.get('end')) ||
                    leftLabel.localeCompare(rightLabel, undefined, {sensitivity: 'base'});
            })
            .forEach(item => content.appendChild(item));
    }));
};

/**
 * Adds the given palette.
 */
Palette.prototype.addPalette = function (id, title, onInit) {
    const palette = document.createElement('div');
    palette.className = 'ui floating dropdown search labeled icon button basic gePaletteDropdown';
    palette.setAttribute('aria-label', title);
    palette.setAttribute('role', 'button');
    palette.setAttribute('tabindex', '0');

    const menu = document.createElement('div');
    menu.className = 'menu';
    menu.setAttribute('role', 'listbox');

    palette.insertAdjacentHTML('afterbegin', '<i class="edit outline icon" aria-hidden="true"></i><input class="search" autocomplete="off" tabindex="0"><div class="default text">Select task</div>');
    palette.appendChild(menu)

    this.container.appendChild(palette);

    onInit(menu);
    onInit = null;

    // Keeps references to the DOM nodes
    if (id != null) {
        this.palettes[id] = [palette];
    }

    $(palette).dropdown({
        action: 'nothing',
        selectOnKeydown: false,
        selectOnBlur: false,
        forceSelection: false,
        onShow: function () {
            window.setTimeout(function () {
                $(palette).find('.menu > .item.selected').removeClass('selected');
            }, 0);
        }
    });

    return palette;
};

/**
 * Adds the given stencil palette.
 */
Palette.prototype.destroy = function () {
    if (this.graph != null) {
        if (this.graph.container != null && this.graph.container.parentNode != null) {
            this.graph.container.parentNode.removeChild(this.graph.container);
        }

        this.graph.destroy();
        this.graph = null;
    }

    if (this.pointerUpHandler != null) {
        mxEvent.removeListener(document, (mxClient.IS_POINTER) ? 'pointerup' : 'mouseup', this.pointerUpHandler);
        this.pointerUpHandler = null;
    }

    if (this.pointerDownHandler != null) {
        mxEvent.removeListener(document, (mxClient.IS_POINTER) ? 'pointerdown' : 'mousedown', this.pointerDownHandler);
        this.pointerDownHandler = null;
    }

    if (this.pointerMoveHandler != null) {
        mxEvent.removeListener(document, (mxClient.IS_POINTER) ? 'pointermove' : 'mousemove', this.pointerMoveHandler);
        this.pointerMoveHandler = null;
    }

    if (this.pointerOutHandler != null) {
        mxEvent.removeListener(document, (mxClient.IS_POINTER) ? 'pointerout' : 'mouseout', this.pointerOutHandler);
        this.pointerOutHandler = null;
    }
};
